import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiUser, 
  FiMail, 
  FiLock, 
  FiSave,
  FiLogOut,
  FiCheckCircle,
  FiMessageCircle,
  FiCopy,
  FiCheck,
  FiEye,
  FiEyeOff,
  FiEdit2,
  FiX,
  FiAlertCircle,
  FiRefreshCw,
  FiCalendar,
  FiDollarSign,
  FiShield,
  FiClock,
  FiInfo,
  FiTrendingUp,
  FiSmartphone,
  FiMonitor,
  FiTablet
} from 'react-icons/fi';
import { accountAPI } from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import useResponsive from '../hooks/useResponsive';

const AccountSettings = ({ onLogout }) => {
  const [accountInfo, setAccountInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const { isMobile, isTablet, isDesktop } = useResponsive();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  const getBalanceDisplay = () => {
    if (!accountInfo) return '0';
    
    const role = accountInfo.role;
    const balances = accountInfo.balances || {};
    
    if (role === 'system' || role === 'super_admin') {
      const phBalance = balances.PH || 0;
      const brBalance = balances.BR || 0;
      return `PH: ${phBalance} | BR: ${brBalance}`;
    } else {
      return `${balances.MMK || 0} MMK`;
    }
  };

  // Color theme based on sidebar account (orange)
  const theme = {
    primary: 'from-orange-600 to-orange-700',
    primaryLight: 'from-orange-500 to-orange-600',
    secondary: 'from-orange-500 to-amber-500',
    bg: 'bg-orange-100 dark:bg-orange-900/30',
    text: 'text-orange-600 dark:text-orange-400',
    border: 'border-orange-200 dark:border-orange-800',
    ring: 'ring-orange-500',
    hover: 'hover:bg-orange-50 dark:hover:bg-orange-900/20',
    gradient: 'from-orange-600 to-amber-600',
    lightBg: 'bg-orange-50 dark:bg-orange-900/20',
    badge: {
      success: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      warning: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      error: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      info: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400'
    }
  };

  useEffect(() => {
    fetchAccountInfo();
  }, []);

  const fetchAccountInfo = async () => {
    try {
      const response = await accountAPI.getMe();
      console.log('Account data:', response.data);
      
      if (response.data.success) {
        const user = response.data.data;
        setAccountInfo(user);
        setFormData({
          name: user.account_name || '',
          email: user.email || '',
          password: '',
          confirmPassword: ''
        });
      }
    } catch (error) {
      console.error('Error fetching account info:', error);
      toast.error('Failed to load account information');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};

    if (formData.password || formData.confirmPassword) {
      if (formData.password.length < 6) {
        errors.password = 'Password must be at least 6 characters';
      }
      if (formData.password !== formData.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
      }
    }

    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleUpdate = async () => {
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    if (formData.password && formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    const updateData = {};
    if (formData.name && formData.name !== accountInfo?.account_name) {
      updateData.name = formData.name;
    }
    if (formData.email && formData.email !== accountInfo?.email) {
      updateData.email = formData.email;
    }
    if (formData.password) {
      updateData.password = formData.password;
    }

    if (Object.keys(updateData).length === 0) {
      toast.error('No changes to update');
      return;
    }

    setUpdating(true);
    try {
      const response = await accountAPI.update(updateData);
      if (response.data.success) {
        toast.success('Account updated successfully');
        setShowEditModal(false);
        setFormData({ ...formData, password: '', confirmPassword: '' });
        setFormErrors({});
        fetchAccountInfo();
      } else {
        toast.error(response.data.message || 'Failed to update account');
      }
    } catch (error) {
      console.error('Error updating account:', error);
      toast.error(error.response?.data?.message || 'Failed to update account');
    } finally {
      setUpdating(false);
    }
  };

  const openEditModal = () => {
    setFormData({
      name: accountInfo?.account_name || '',
      email: accountInfo?.email || '',
      password: '',
      confirmPassword: ''
    });
    setFormErrors({});
    setShowEditModal(true);
  };

  const getTelegramUsername = () => {
    if (!accountInfo?.telegram) return null;
    
    if (typeof accountInfo.telegram === 'object' && accountInfo.telegram.user_name) {
      return `@${accountInfo.telegram.user_name}`;
    }
    
    if (typeof accountInfo.telegram === 'string') {
      return accountInfo.telegram;
    }
    
    return null;
  };

  const getTelegramLink = () => {
    const username = getTelegramUsername();
    if (!username) return '#';
    
    const cleanUsername = username.replace('@', '');
    return `https://t.me/${cleanUsername}`;
  };

  const copyToClipboard = () => {
    const username = getTelegramUsername();
    if (!username) return;
    
    navigator.clipboard.writeText(username).then(() => {
      setCopied(true);
      toast.success('Telegram username copied!');
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      toast.error('Failed to copy');
    });
  };

  const telegramUsername = getTelegramUsername();

  const getRoleBadgeColor = (role) => {
    switch(role) {
      case 'system':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'super_admin':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400';
      case 'reseller':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      default:
        return theme.badge.info;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-orange-200 dark:border-orange-900 rounded-full"></div>
          <div className="w-16 h-16 border-4 border-orange-600 border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-24 md:pb-6">
      {/* Header Section */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`bg-gradient-to-r ${theme.gradient} rounded-2xl p-6 sm:p-8 shadow-lg`}
      >
        <div className="flex items-center gap-4">
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center"
          >
            <FiUser className="w-7 h-7 text-white" />
          </motion.div>
          <div>
            <motion.h1
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="text-2xl md:text-3xl font-bold text-white"
            >
              Account Settings
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-orange-100 text-sm mt-1"
            >
              Manage your profile and account information
            </motion.p>
          </div>
        </div>
      </motion.div>

      {/* Profile Header Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700"
      >
        <div className="flex flex-col md:flex-row items-center gap-6">
          <motion.div
            whileHover={{ scale: 1.05 }}
            className={`w-24 h-24 rounded-2xl bg-gradient-to-r ${theme.gradient} flex items-center justify-center text-white text-4xl font-bold shadow-lg`}
          >
            {accountInfo?.account_name?.charAt(0).toUpperCase() || 'A'}
          </motion.div>
          
          <div className="flex-1 text-center md:text-left">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
              {accountInfo?.account_name}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-3 flex items-center justify-center md:justify-start gap-2">
              <FiMail className="w-4 h-4" />
              {accountInfo?.email}
            </p>
            <div className="flex flex-wrap gap-2 justify-center md:justify-start">
              <span className={`px-3 py-1.5 rounded-lg text-xs font-medium ${getRoleBadgeColor(accountInfo?.role)} capitalize flex items-center gap-1`}>
                <FiShield className="w-3 h-3" />
                {accountInfo?.role?.replace('_', ' ') || 'user'}
              </span>
              <span className={`px-3 py-1.5 rounded-lg text-xs font-medium ${theme.badge.success} flex items-center gap-1`}>
                <FiDollarSign className="w-3 h-3" />
                {getBalanceDisplay()}
              </span>
            </div>
          </div>

          {/* Edit Profile Button */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={openEditModal}
            className={`px-6 py-3 bg-gradient-to-r ${theme.primary} text-white rounded-xl hover:from-orange-700 hover:to-orange-800 transition-all shadow-lg shadow-orange-500/20 flex items-center gap-2`}
          >
            <FiEdit2 className="w-4 h-4" />
            <span>Edit Profile</span>
          </motion.button>
        </div>
      </motion.div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Member Since', value: new Date(accountInfo?.created_at).toLocaleDateString(), icon: FiCalendar, color: 'orange' },
          { label: 'Status', value: 'Active', icon: FiCheckCircle, color: 'green' },
          { label: 'Role', value: accountInfo?.role?.replace('_', ' ') || 'user', icon: FiShield, color: 'purple' },
          { label: 'Balance', value: getBalanceDisplay(), icon: FiDollarSign, color: 'amber' }
        ].map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 + index * 0.05 }}
            className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-md transition-all"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">{stat.label}</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white mt-1">{stat.value}</p>
              </div>
              <div className={`w-8 h-8 bg-${stat.color}-100 dark:bg-${stat.color}-900/30 rounded-lg flex items-center justify-center`}>
                <stat.icon className={`w-4 h-4 text-${stat.color}-600 dark:text-${stat.color}-400`} />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Contact Information Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
          <div className={`p-2 ${theme.bg} rounded-lg mr-3`}>
            <FiMail className={`w-5 h-5 ${theme.text}`} />
          </div>
          Contact Information
        </h3>
        
        <div className="space-y-4">
          <div className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <FiMail className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-500 dark:text-gray-400">Email Address</p>
              <p className="text-gray-900 dark:text-white font-medium break-all">{accountInfo?.email}</p>
            </div>
          </div>
          
          <div className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
            <div className={`p-2 ${theme.bg} rounded-lg`}>
              <FiMessageCircle className={`w-5 h-5 ${theme.text}`} />
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-500 dark:text-gray-400">Telegram</p>
              <div className="flex flex-wrap items-center gap-3 mt-1">
                {telegramUsername ? (
                  <>
                    <span className="text-gray-900 dark:text-white font-medium">
                      {telegramUsername}
                    </span>
                    {accountInfo?.telegram?.is_active === 1 && (
                      <span className={`px-2 py-0.5 ${theme.badge.success} rounded-full text-xs flex items-center gap-1`}>
                        <FiCheckCircle className="w-3 h-3" />
                        Active
                      </span>
                    )}
                    <div className="flex items-center gap-1">
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={copyToClipboard}
                        className={`p-2 ${theme.hover} rounded-lg transition-colors ${theme.text}`}
                        title="Copy Telegram username"
                      >
                        {copied ? (
                          <FiCheck className="w-4 h-4 text-green-600" />
                        ) : (
                          <FiCopy className="w-4 h-4" />
                        )}
                      </motion.button>
                      <motion.a 
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        href={getTelegramLink()}
                        target="_blank" 
                        rel="noopener noreferrer"
                        className={`p-2 ${theme.hover} rounded-lg transition-colors ${theme.text}`}
                        title="Open in Telegram"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69.01-.03.01-.14-.07-.2-.08-.06-.19-.04-.27-.02-.12.02-1.96 1.25-5.54 3.69-.52.36-1 .53-1.42.52-.47-.01-1.37-.26-2.04-.48-.82-.27-1.47-.42-1.42-.88.03-.24.36-.48 1.03-.74 4.16-1.81 6.94-3.01 8.33-3.59 3.97-1.66 4.79-1.95 5.33-1.96.12 0 .38.03.55.18.14.12.18.28.2.41-.02.13-.05.41-.05.41z" />
                        </svg>
                      </motion.a>
                    </div>
                  </>
                ) : (
                  <span className="text-gray-500 dark:text-gray-500">Not connected</span>
                )}
              </div>
              {accountInfo?.telegram?.telegram_linked_at && (
                <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                  <FiClock className="w-3 h-3" />
                  Linked: {new Date(accountInfo.telegram.telegram_linked_at).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Logout Button - Only for mobile */}
      {isMobile && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={onLogout}
            className="w-full flex items-center justify-center px-6 py-4 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl hover:from-red-700 hover:to-red-800 transition-all shadow-lg shadow-red-500/20"
          >
            <FiLogOut className="w-5 h-5 mr-2" />
            Sign Out
          </motion.button>
        </motion.div>
      )}

      {/* Edit Profile Modal */}
      <AnimatePresence>
        {showEditModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowEditModal(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
            />
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
                {/* Modal Header */}
                <div className={`bg-gradient-to-r ${theme.gradient} px-6 py-5`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <motion.div
                        initial={{ scale: 0, rotate: -180 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ type: "spring", stiffness: 200 }}
                        className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center"
                      >
                        <FiEdit2 className="w-5 h-5 text-white" />
                      </motion.div>
                      <div>
                        <h3 className="text-lg font-semibold text-white">
                          Edit Profile
                        </h3>
                        <p className="text-sm text-orange-100">
                          Update your account information
                        </p>
                      </div>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.1, rotate: 90 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => setShowEditModal(false)}
                      className="p-2 hover:bg-white/20 rounded-xl transition-colors"
                    >
                      <FiX className="w-5 h-5 text-white" />
                    </motion.button>
                  </div>
                </div>

                {/* Modal Body */}
                <div className="flex-1 overflow-y-auto scrollbar-hide p-6 bg-gray-50 dark:bg-gray-900">
                  <div className="space-y-5">
                    {/* Account Name Field */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Account Name
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className={`w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 rounded-xl text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 ${
                          formErrors.name 
                            ? 'border-red-300 focus:ring-red-500' 
                            : 'border-gray-200 dark:border-gray-700 hover:border-orange-300'
                        }`}
                        placeholder="Enter account name"
                      />
                    </div>

                    {/* Email Field */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className={`w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 rounded-xl text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 ${
                          formErrors.email 
                            ? 'border-red-300 focus:ring-red-500' 
                            : 'border-gray-200 dark:border-gray-700 hover:border-orange-300'
                        }`}
                        placeholder="Enter email"
                      />
                      {formErrors.email && (
                        <motion.p
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="mt-2 text-xs text-red-500 flex items-center gap-1"
                        >
                          <FiAlertCircle className="w-3 h-3" />
                          {formErrors.email}
                        </motion.p>
                      )}
                    </div>

                    {/* Password Field */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        New Password (Optional)
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          className={`w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 rounded-xl text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 pr-10 ${
                            formErrors.password 
                              ? 'border-red-300 focus:ring-red-500' 
                              : 'border-gray-200 dark:border-gray-700 hover:border-orange-300'
                          }`}
                          placeholder="Enter new password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        >
                          {showPassword ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
                        </button>
                      </div>
                      {formErrors.password && (
                        <motion.p
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="mt-2 text-xs text-red-500 flex items-center gap-1"
                        >
                          <FiAlertCircle className="w-3 h-3" />
                          {formErrors.password}
                        </motion.p>
                      )}
                    </div>

                    {/* Confirm Password Field */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Confirm Password
                      </label>
                      <div className="relative">
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          value={formData.confirmPassword}
                          onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                          className={`w-full px-4 py-3 bg-white dark:bg-gray-800 border-2 rounded-xl text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 pr-10 ${
                            formErrors.confirmPassword 
                              ? 'border-red-300 focus:ring-red-500' 
                              : 'border-gray-200 dark:border-gray-700 hover:border-orange-300'
                          }`}
                          placeholder="Confirm new password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        >
                          {showConfirmPassword ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
                        </button>
                      </div>
                      {formErrors.confirmPassword && (
                        <motion.p
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="mt-2 text-xs text-red-500 flex items-center gap-1"
                        >
                          <FiAlertCircle className="w-3 h-3" />
                          {formErrors.confirmPassword}
                        </motion.p>
                      )}
                    </div>

                    {/* Info Message */}
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl">
                      <p className="text-xs text-blue-600 dark:text-blue-400 flex items-center gap-2">
                        <FiInfo className="w-4 h-4 flex-shrink-0" />
                        Leave password fields empty if you don't want to change your password
                      </p>
                    </div>
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="border-t-2 border-gray-100 dark:border-gray-700 px-6 py-4 bg-white dark:bg-gray-800">
                  <div className="flex justify-end gap-3">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setShowEditModal(false)}
                      className="px-6 py-3 rounded-xl font-medium text-sm border-2 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all"
                      disabled={updating}
                    >
                      Cancel
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleUpdate}
                      disabled={updating}
                      className={`px-8 py-3 bg-gradient-to-r ${theme.primary} text-white rounded-xl font-medium text-sm hover:from-orange-700 hover:to-orange-800 transition-all shadow-lg shadow-orange-500/20 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {updating ? (
                        <>
                          <FiRefreshCw className="w-4 h-4 animate-spin" />
                          <span>Processing...</span>
                        </>
                      ) : (
                        <>
                          <FiSave className="w-4 h-4" />
                          <span>Save Changes</span>
                        </>
                      )}
                    </motion.button>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

export default AccountSettings;